/**
 * Image Source Constants
 *
 * Constants for selecting between stock photos and AI-generated images.
 * Supports individual provider selection within each source type.
 *
 * The provider set and its capability flags are NOT written here. They are
 * generated from `IMAGE_PROVIDER_FEATURES` (`constants/image/providers.py`) into
 * `shared_constants.json` by `python scripts/generate_shared_constants.py`,
 * which `npm run generate:all` runs before every dev server, build and
 * type-check. This file reads them (RULE #-2: one table, not two kept in step).
 */

import sharedConstants from './shared_constants.json';

export type ImageSourceType = 'stock' | 'ai';

/** Available stock image providers */
export type StockImageProvider = 'pexels' | 'pixabay';

/**
 * Capability flags the BACKEND owns, generated from `IMAGE_PROVIDER_FEATURES`.
 *
 * This used to be a hand-written copy inside `PROVIDER_FEATURES` below, and the
 * copy was wrong: it published `supportsNegativePrompt: false` for all six
 * `openrouter-*` providers while the backend threaded the field end to end, so
 * the UI hid a control that works. Reading the generated table is the only way
 * the two cannot disagree again.
 */
const GENERATED_PROVIDER_FEATURES = sharedConstants.image_provider_features;

/**
 * Available AI image providers — the keys of the generated table, so a provider
 * added in Python arrives here without anyone editing this file. Every
 * `Record<AIImageProvider, ...>` below (and in `imageTimeEstimator.ts`) then
 * fails to compile until it is given a row, which is the loud failure a silent
 * omission deserves.
 */
export type AIImageProvider = keyof typeof GENERATED_PROVIDER_FEATURES;

export interface ImageProviderOption {
  value: string;
  labelKey: string;
  enabled: boolean;
  pricing?: string;
  descriptionKey?: string;
  speed?: 'fastest' | 'fast' | 'medium' | 'slow';
  /**
   * Factual badges only. Marketing labels (BEST/CHEAP/PREMIUM/VALUE/WARN) were
   * removed because they invented a different vocabulary per provider lot and
   * are now communicated through the sort order. Keep: `LOCAL` (runs on user's
   * own hardware), `4K` (supports 4K output).
   */
  badge?: 'LOCAL' | '4K';
  /**
   * Subjective output-quality tier, 1 (basic) to 5 (state-of-the-art).
   * Used by the sort dropdown in `ImageProviderSelector`. Optional because
   * stock-photo providers don't have a meaningful AI-quality score.
   */
  quality?: 1 | 2 | 3 | 4 | 5;
  /** Whether provider allows NSFW content (no safety filter) */
  nsfw?: boolean;
  /** Whether this is the recommended default for the collapsed preview. */
  recommended?: boolean;
}

export interface ImageSourceOption {
  value: ImageSourceType;
  labelKey: string;
  descriptionKey: string;
  providers: ImageProviderOption[];
  icon: string;
}

/** Stock image provider options */
export const STOCK_IMAGE_PROVIDERS: ImageProviderOption[] = [
  { value: 'pexels', labelKey: 'imageSource.provider.pexels', enabled: true, pricing: 'Free' },
  { value: 'pixabay', labelKey: 'imageSource.provider.pixabay', enabled: true, pricing: 'Free' },
];

/**
 * AI image provider options. Pricing in tokens (1 token = $0.001 USD).
 *
 * Array order is the default presentation order (price ascending — locals
 * first because they're free on desktop, then cheapest cloud → most expensive).
 * The `ImageProviderSelector` component lets the user re-sort by price, speed
 * or quality at runtime, so this order is only the initial layout.
 *
 * Badges are intentionally minimal: only `LOCAL` (runs on user's hardware)
 * and `4K` (supports 4K output). Marketing labels (BEST/CHEAP/PREMIUM/VALUE/
 * WARN/AUTO/FAST) were removed because they were inconsistent across batches
 * and added visual noise without communicating anything the sort doesn't.
 */
export const AI_IMAGE_PROVIDERS: ImageProviderOption[] = [
  {
    value: 'comfyui-sd',
    labelKey: 'imageSource.provider.comfyuiSd',
    descriptionKey: 'imageSource.providerDescription.comfyuiSd',
    enabled: true,
    pricing: 'Free',
    speed: 'fast',
    badge: 'LOCAL',
    quality: 1,
    nsfw: true,
  },
  {
    value: 'fooocus',
    labelKey: 'imageSource.provider.fooocus',
    descriptionKey: 'imageSource.providerDescription.fooocus',
    enabled: true,
    pricing: 'Free / ~10',
    speed: 'medium',
    badge: 'LOCAL',
    quality: 1,
    nsfw: true,
  },
  {
    value: 'black-forest-labs-flux-1-schnell',
    labelKey: 'imageSource.provider.fluxSchnell',
    descriptionKey: 'imageSource.providerDescription.fluxSchnell',
    enabled: true,
    pricing: '~3',
    speed: 'fastest',
    quality: 3,
  },
  {
    value: 'z-image-turbo',
    labelKey: 'imageSource.provider.zImageTurbo',
    descriptionKey: 'imageSource.providerDescription.zImageTurbo',
    enabled: true,
    pricing: '5',
    speed: 'fastest',
    quality: 2,
  },
  {
    value: 'p-image-t2i',
    labelKey: 'imageSource.provider.pruna',
    descriptionKey: 'imageSource.providerDescription.pruna',
    enabled: true,
    pricing: '5',
    speed: 'fast',
    quality: 3,
  },
  {
    value: 'black-forest-labs-flux-1-dev',
    labelKey: 'imageSource.provider.fluxDev',
    descriptionKey: 'imageSource.providerDescription.fluxDev',
    enabled: true,
    pricing: '~21',
    speed: 'slow',
    quality: 4,
    nsfw: true,
  },
  {
    value: 'qwen-image-t2i',
    labelKey: 'imageSource.provider.qwen',
    descriptionKey: 'imageSource.providerDescription.qwen',
    enabled: true,
    pricing: '~21',
    speed: 'medium',
    quality: 3,
  },
  {
    value: 'qwen-image-t2i-lora',
    labelKey: 'imageSource.provider.qwenLora',
    descriptionKey: 'imageSource.providerDescription.qwenLora',
    enabled: true,
    pricing: '~21',
    speed: 'medium',
    quality: 3,
  },
  {
    value: 'wan-2-6-t2i',
    labelKey: 'imageSource.provider.wan26',
    descriptionKey: 'imageSource.providerDescription.wan26',
    enabled: true,
    pricing: '~21',
    speed: 'medium',
    quality: 3,
  },
  {
    value: 'seedream-v4-t2i',
    labelKey: 'imageSource.provider.seedream',
    descriptionKey: 'imageSource.providerDescription.seedream',
    enabled: true,
    pricing: '27',
    speed: 'medium',
    badge: '4K',
    quality: 4,
  },
  // OpenRouter multimodal image models. `pricing` = fixed upfront tokens
  // (fixed_cost_usd × 1.5 markup ÷ $0.001 per token).
  {
    value: 'openrouter-nano-banana-25',
    labelKey: 'imageSource.provider.openrouterNanoBanana25',
    descriptionKey: 'imageSource.providerDescription.openrouterNanoBanana25',
    enabled: true,
    pricing: '71',
    speed: 'fastest',
    quality: 4,
  },
  {
    value: 'openrouter-gpt-5-image-mini',
    labelKey: 'imageSource.provider.openrouterGpt5ImageMini',
    descriptionKey: 'imageSource.providerDescription.openrouterGpt5ImageMini',
    enabled: true,
    pricing: '81',
    speed: 'slow',
    quality: 4,
  },
  {
    value: 'openrouter-nano-banana-flash',
    labelKey: 'imageSource.provider.openrouterNanoBananaFlash',
    descriptionKey: 'imageSource.providerDescription.openrouterNanoBananaFlash',
    enabled: true,
    pricing: '120',
    speed: 'fast',
    quality: 5,
    recommended: true,
  },
  {
    value: 'openrouter-nano-banana-pro',
    labelKey: 'imageSource.provider.openrouterNanoBananaPro',
    descriptionKey: 'imageSource.providerDescription.openrouterNanoBananaPro',
    enabled: true,
    pricing: '249',
    speed: 'medium',
    quality: 5,
  },
  {
    value: 'openrouter-gpt-5-image',
    labelKey: 'imageSource.provider.openrouterGpt5Image',
    descriptionKey: 'imageSource.providerDescription.openrouterGpt5Image',
    enabled: true,
    pricing: '360',
    speed: 'slow',
    quality: 4,
  },
  {
    value: 'openrouter-gpt-54-image-2',
    labelKey: 'imageSource.provider.openrouterGpt54Image2',
    descriptionKey: 'imageSource.providerDescription.openrouterGpt54Image2',
    enabled: true,
    pricing: '428',
    speed: 'slow',
    quality: 5,
  },
];

/**
 * Available image source options
 */
export const IMAGE_SOURCE_OPTIONS: ImageSourceOption[] = [
  {
    value: 'stock',
    labelKey: 'imageSource.stock.label',
    descriptionKey: 'imageSource.stock.description',
    providers: STOCK_IMAGE_PROVIDERS,
    icon: 'image',
  },
  {
    value: 'ai',
    labelKey: 'imageSource.ai.label',
    descriptionKey: 'imageSource.ai.description',
    providers: AI_IMAGE_PROVIDERS,
    icon: 'sparkles',
  },
];

/**
 * Default image source (backward compatible with existing behavior)
 */
export const DEFAULT_IMAGE_SOURCE: ImageSourceType = 'stock';

/** Default stock image provider */
export const DEFAULT_STOCK_IMAGE_PROVIDER: StockImageProvider = 'pexels';

/** Default AI image provider */
export const DEFAULT_AI_IMAGE_PROVIDER: AIImageProvider = 'fooocus';

/**
 * i18n key naming this operation in BILLING surfaces (the insufficient-tokens
 * modal, the pipeline cost breakdown).
 *
 * One key, one wording: the config form and the page both open the same
 * purchase modal, and two hand-typed labels would read as two different
 * operations to the user being asked to pay for one.
 */
export const IMAGE_GENERATION_OPERATION_LABEL_KEY = 'tokens.component_image_generation';

/**
 * OpenRouter-backed image providers.
 *
 * Mirrors `OPENROUTER_IMAGE_PROVIDER_IDS` in `constants/image/openrouter_image.py`
 * — keep the IDs identical (RULE #1.5).
 */
export const OPENROUTER_IMAGE_PROVIDERS: readonly AIImageProvider[] = [
  'openrouter-nano-banana-pro',
  'openrouter-nano-banana-flash',
  'openrouter-gpt-54-image-2',
  'openrouter-nano-banana-25',
  'openrouter-gpt-5-image-mini',
  'openrouter-gpt-5-image',
] as const;

/**
 * Providers the DESKTOP backend dispatches itself, so the request must go to
 * the local API rather than being routed to Cloud Run.
 *
 * Mirrors `DESKTOP_DISPATCHABLE_IMAGE_PROVIDERS` in
 * `api_server/services/image_generation_service.py` — the two lists describe
 * one fact and must not drift (RULE #-2, RULE #1.5).
 *
 * They HAD drifted: this set held only fooocus + comfyui-sd, so every
 * `openrouter-*` generation was routed to Cloud Run. That is not merely a
 * longer network path — off-cloud those providers spend the user's OWN
 * OPENROUTER_API_KEY and cost nothing, while the Cloud Run path spends
 * Asgard's key and bills the account with margin. The stale copy was quietly
 * charging users for work their own key would have done for free.
 *
 * "Dispatched here" is NOT "runs here": fooocus reaches RunPod and
 * `openrouter-*` reaches OpenRouter. Only comfyui-sd runs on this machine.
 */
export const DESKTOP_DISPATCHABLE_IMAGE_PROVIDERS: ReadonlySet<AIImageProvider> =
  new Set<AIImageProvider>(['fooocus', 'comfyui-sd', ...OPENROUTER_IMAGE_PROVIDERS]);

/**
 * Get image source option by value
 */
export const getImageSourceOption = (value: ImageSourceType): ImageSourceOption | undefined => {
  return IMAGE_SOURCE_OPTIONS.find((opt) => opt.value === value);
};

/**
 * API endpoint for media search
 */
export const MEDIA_SEARCH_ENDPOINT = '/api/v1/media/search/images';

// =============================================================================
// PROVIDER FEATURE FLAGS
// =============================================================================

/**
 * The knobs the backend table does NOT define, so they cannot be generated.
 *
 * `constants/image/providers.py` says which controls a provider honours; it
 * says nothing about slider bounds, and nothing about whether the request
 * payload carries `output_format`. Those five values stay here, and they are
 * pinned rather than generated for one reason each:
 *
 * - `supportsOutputFormat` is a property of the payload each provider's RunPod
 *   endpoint accepts (`build_public_endpoint_payload`), which no constant
 *   currently states. Restating it in Python would be moving the guess, not
 *   removing it.
 * - the four numbers are UI slider bounds, meaningful only for the four
 *   providers that support steps or guidance at all.
 *
 * They are typed `Record<AIImageProvider, ...>` against the GENERATED provider
 * set, so a provider added in Python cannot silently arrive without them.
 */
export interface UiOnlyProviderKnobs {
  /** Whether the backend payload for this provider honors output_format (png/jpg/webp). */
  supportsOutputFormat: boolean;
  /** Default num_steps when selecting this provider */
  defaultNumSteps: number;
  /** Max num_steps slider value */
  maxNumSteps: number;
  /** Default guidance_scale when selecting this provider */
  defaultGuidance: number;
  /** Max guidance_scale slider value */
  maxGuidance: number;
}

const UI_ONLY_PROVIDER_KNOBS: Record<AIImageProvider, UiOnlyProviderKnobs> = {
  fooocus: {
    supportsOutputFormat: false,
    defaultNumSteps: 30,
    maxNumSteps: 100,
    defaultGuidance: 4.0,
    maxGuidance: 30,
  },
  'comfyui-sd': {
    supportsOutputFormat: false,
    defaultNumSteps: 20,
    maxNumSteps: 50,
    defaultGuidance: 7.0,
    maxGuidance: 20,
  },
  'z-image-turbo': {
    supportsOutputFormat: true,
    defaultNumSteps: 30,
    maxNumSteps: 100,
    defaultGuidance: 4.0,
    maxGuidance: 30,
  },
  'black-forest-labs-flux-1-schnell': {
    supportsOutputFormat: true,
    defaultNumSteps: 4,
    maxNumSteps: 12,
    defaultGuidance: 7.0,
    maxGuidance: 8,
  },
  'black-forest-labs-flux-1-dev': {
    supportsOutputFormat: true,
    defaultNumSteps: 28,
    maxNumSteps: 50,
    defaultGuidance: 7.0,
    maxGuidance: 20,
  },
  'seedream-v4-t2i': {
    supportsOutputFormat: false,
    defaultNumSteps: 30,
    maxNumSteps: 100,
    defaultGuidance: 4.0,
    maxGuidance: 30,
  },
  'p-image-t2i': {
    supportsOutputFormat: false,
    defaultNumSteps: 30,
    maxNumSteps: 100,
    defaultGuidance: 4.0,
    maxGuidance: 30,
  },
  'qwen-image-t2i': {
    supportsOutputFormat: false,
    defaultNumSteps: 30,
    maxNumSteps: 100,
    defaultGuidance: 4.0,
    maxGuidance: 30,
  },
  'qwen-image-t2i-lora': {
    supportsOutputFormat: false,
    defaultNumSteps: 30,
    maxNumSteps: 100,
    defaultGuidance: 4.0,
    maxGuidance: 30,
  },
  'wan-2-6-t2i': {
    supportsOutputFormat: false,
    defaultNumSteps: 30,
    maxNumSteps: 100,
    defaultGuidance: 4.0,
    maxGuidance: 30,
  },
  'openrouter-nano-banana-pro': {
    supportsOutputFormat: false,
    defaultNumSteps: 0,
    maxNumSteps: 0,
    defaultGuidance: 0,
    maxGuidance: 0,
  },
  'openrouter-nano-banana-flash': {
    supportsOutputFormat: false,
    defaultNumSteps: 0,
    maxNumSteps: 0,
    defaultGuidance: 0,
    maxGuidance: 0,
  },
  'openrouter-gpt-54-image-2': {
    supportsOutputFormat: false,
    defaultNumSteps: 0,
    maxNumSteps: 0,
    defaultGuidance: 0,
    maxGuidance: 0,
  },
  'openrouter-nano-banana-25': {
    supportsOutputFormat: false,
    defaultNumSteps: 0,
    maxNumSteps: 0,
    defaultGuidance: 0,
    maxGuidance: 0,
  },
  'openrouter-gpt-5-image-mini': {
    supportsOutputFormat: false,
    defaultNumSteps: 0,
    maxNumSteps: 0,
    defaultGuidance: 0,
    maxGuidance: 0,
  },
  'openrouter-gpt-5-image': {
    supportsOutputFormat: false,
    defaultNumSteps: 0,
    maxNumSteps: 0,
    defaultGuidance: 0,
    maxGuidance: 0,
  },
};

/**
 * Provider feature support mapping.
 * Determines which UI components to show for each provider.
 *
 * `supportsStyles` / `supportsModels` / `supportsNegativePrompt` /
 * `supportsNumSteps` / `supportsGuidance` / `supportsSharpness` /
 * `supportsPerformance` / `supportsSeed` are generated from
 * `IMAGE_PROVIDER_FEATURES`; the rest come from `UI_ONLY_PROVIDER_KNOBS` above.
 * Two of the generated ones are answers nobody could have guessed and both were
 * once published backwards, which is why they are read and not written:
 * `supportsNegativePrompt` is TRUE for the `openrouter-*` models (they have no
 * such field, but the backend appends "Avoid: …" to the prompt they read), and
 * `supportsSeed` is FALSE for them (a seed is accepted upstream and never pins
 * the image). Both measured 2026-08-21.
 */
export type ProviderFeatures = (typeof GENERATED_PROVIDER_FEATURES)[AIImageProvider] &
  UiOnlyProviderKnobs;

export const PROVIDER_FEATURES: Record<AIImageProvider, ProviderFeatures> = Object.fromEntries(
  (Object.keys(GENERATED_PROVIDER_FEATURES) as AIImageProvider[]).map((provider) => [
    provider,
    { ...GENERATED_PROVIDER_FEATURES[provider], ...UI_ONLY_PROVIDER_KNOBS[provider] },
  ])
) as Record<AIImageProvider, ProviderFeatures>;

/**
 * Get feature support for a provider
 */
export function getProviderFeatures(provider: AIImageProvider) {
  return PROVIDER_FEATURES[provider] || PROVIDER_FEATURES.fooocus;
}

// =============================================================================
// DEFAULT IMAGE GENERATION CONFIG FACTORY
// =============================================================================

/**
 * Shape of the controlled config consumed by `ImageGeneratorPanel` /
 * `ImageConfigForm`. Declared here (and re-exported as `ImageGenerationConfig`
 * in `types/`) so the default factory below has no cross-module import cycle.
 */
export interface ImageGenConfigShape {
  prompt: string;
  negative_prompt: string;
  styles: string[];
  resolution: string;
  width?: number;
  height?: number;
  num_images: number;
  seed: number;
  guidance_scale: number;
  num_steps: number;
  sharpness: number;
  performance: 'Speed' | 'Quality' | 'Extreme Speed' | 'Lightning' | 'Hyper-SD';
  output_format: 'png' | 'jpg' | 'webp';
  async_process: boolean;
  provider: AIImageProvider;
  model?: string;
}

/**
 * Build a fresh default image-generation config. Single source of truth shared
 * by every AI-image surface (Text-to-Image, Posts & Carousels, YouTube
 * thumbnail) so they all start model-aware and consistent (RULE #-2).
 *
 * Overrides let a surface pin a different starting resolution (e.g. the YouTube
 * thumbnail wants 1280x720) or seed an empty style list, without diverging on
 * any other knob.
 */
export function createDefaultImageConfig(
  overrides: Partial<ImageGenConfigShape> = {}
): ImageGenConfigShape {
  const provider = overrides.provider ?? DEFAULT_AI_IMAGE_PROVIDER;
  const features = getProviderFeatures(provider);
  return {
    prompt: '',
    negative_prompt: '',
    styles: [],
    resolution: '1152x896',
    num_images: 1,
    seed: -1,
    guidance_scale: features.defaultGuidance,
    num_steps: features.defaultNumSteps,
    sharpness: 2.0,
    performance: 'Speed',
    output_format: 'png',
    async_process: true,
    provider,
    model: undefined,
    ...overrides,
  };
}

// =============================================================================
// FOOOCUS-ONLY STYLES
// =============================================================================

/**
 * Styles that only work with Fooocus (internal LoRA embeddings).
 * These are hidden from the style selector when using other providers.
 */
export const FOOOCUS_ONLY_STYLES = [
  'Fooocus V2',
  'Fooocus Enhance',
  'Fooocus Semi Realistic',
  'Fooocus Sharp',
  'Fooocus Masterpiece',
  'Fooocus Photograph',
  'Fooocus Negative',
  'Fooocus Cinematic',
  'Fooocus Pony',
] as const;
